import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  History,
  Search,
  Flight,
  Receipt,
  NavigateNext,
  TrendingUp
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = `${API_URL}/api/v1`;

/**
 * AuditLanding - Split-view audit landing page
 * 
 * Layout:
 * - Left side: Rotations list
 * - Right side: Timecards list
 * 
 * Features:
 * - Search/filter each side independently
 * - Click entity to view version history
 * - Shows version count badge
 */
const AuditLanding = () => {
  const navigate = useNavigate();

  // State for rotations
  const [rotations, setRotations] = useState([]);
  const [rotationsLoading, setRotationsLoading] = useState(true);
  const [rotationsError, setRotationsError] = useState(null);
  const [rotationsSearch, setRotationsSearch] = useState('');

  // State for timecards
  const [timecards, setTimecards] = useState([]);
  const [timecardsLoading, setTimecardsLoading] = useState(true);
  const [timecardsError, setTimecardsError] = useState(null);
  const [timecardsSearch, setTimecardsSearch] = useState('');

  // Fetch rotations on mount
  useEffect(() => {
    const fetchRotations = async () => {
      setRotationsLoading(true);
      setRotationsError(null);

      try {
        // Fetch all rotations (we'll add pagination later if needed)
        const response = await axios.get(`${API_BASE}/rotations`, {
          params: {
            limit: 100,
            include_deleted: false
          }
        });

        // Group by rotation_number to count versions
        const grouped = {};
        response.data.forEach(rotation => {
          const number = rotation.rotation_number;
          if (!grouped[number]) {
            grouped[number] = {
              rotation_number: number,
              employee_id: rotation.employee_id,
              employee_name: rotation.employee_name,
              start_date: rotation.rotation_start_date,
              end_date: rotation.rotation_end_date,
              version_count: 0,
              latest_version: rotation
            };
          }
          grouped[number].version_count++;
        });

        setRotations(Object.values(grouped));
      } catch (err) {
        setRotationsError(err.response?.data?.detail || 'Failed to load rotations');
      } finally {
        setRotationsLoading(false);
      }
    };

    fetchRotations();
  }, []);

  // Fetch timecards on mount
  useEffect(() => {
    const fetchTimecards = async () => {
      setTimecardsLoading(true);
      setTimecardsError(null);

      try {
        // Fetch all timecards
        const response = await axios.get(`${API_BASE}/timecards`, {
          params: {
            limit: 100,
            include_deleted: false
          }
        });

        // Group by timecard_number to count versions
        const grouped = {};
        response.data.forEach(timecard => {
          const number = timecard.timecard_number || timecard.id;
          if (!grouped[number]) {
            grouped[number] = {
              timecard_number: number,
              employee_id: timecard.employee_id,
              employee_name: timecard.employee_name,
              bid_period: timecard.bid_period,
              period_start: timecard.period_start_date,
              period_end: timecard.period_end_date,
              version_count: 0,
              latest_version: timecard
            };
          }
          grouped[number].version_count++;
        });

        setTimecards(Object.values(grouped));
      } catch (err) {
        setTimecardsError(err.response?.data?.detail || 'Failed to load timecards');
      } finally {
        setTimecardsLoading(false);
      }
    };

    fetchTimecards();
  }, []);

  // Filter rotations by search
  const filteredRotations = rotations.filter(r => {
    if (!rotationsSearch) return true;
    const search = rotationsSearch.toLowerCase();
    return (
      r.rotation_number?.toLowerCase().includes(search) ||
      r.employee_id?.toLowerCase().includes(search) ||
      r.employee_name?.toLowerCase().includes(search)
    );
  });

  // Filter timecards by search
  const filteredTimecards = timecards.filter(t => {
    if (!timecardsSearch) return true;
    const search = timecardsSearch.toLowerCase();
    return (
      t.timecard_number?.toLowerCase().includes(search) ||
      t.employee_id?.toLowerCase().includes(search) ||
      t.employee_name?.toLowerCase().includes(search) ||
      t.bid_period?.toLowerCase().includes(search)
    );
  });

  // Navigate to rotation audit detail
  const handleRotationClick = (rotation) => {
    navigate(`/audit?type=rotation&number=${rotation.rotation_number}`);
  };

  // Navigate to timecard audit detail
  const handleTimecardClick = (timecard) => {
    navigate(`/audit?type=timecard&number=${timecard.timecard_number}`);
  };

  // Render entity card
  const EntityCard = ({ entity, type, onClick }) => {
    const isRotation = type === 'rotation';
    
    return (
      <Card sx={{ mb: 2 }}>
        <CardActionArea onClick={onClick}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isRotation ? <Flight color="primary" /> : <Receipt color="primary" />}
                <Typography variant="h6">
                  {isRotation ? entity.rotation_number : entity.timecard_number}
                </Typography>
              </Box>
              <Chip 
                label={`${entity.version_count} version${entity.version_count !== 1 ? 's' : ''}`}
                size="small"
                color="secondary"
                icon={<History />}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {entity.employee_name || entity.employee_id}
            </Typography>

            {isRotation ? (
              <Typography variant="caption" color="text.secondary">
                {entity.start_date} to {entity.end_date}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {entity.bid_period} • {entity.period_start} to {entity.period_end}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <TrendingUp fontSize="large" color="primary" />
          <Typography variant="h4">
            Audit Center
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View version history and compare changes for rotations and timecards
        </Typography>
      </Box>

      {/* Split-view grid */}
      <Grid container spacing={3}>
        {/* Left side: Rotations */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Flight color="primary" />
              <Typography variant="h5">
                Rotations
              </Typography>
              <Chip 
                label={filteredRotations.length} 
                size="small" 
                color="primary" 
              />
            </Box>

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search rotations..."
              value={rotationsSearch}
              onChange={(e) => setRotationsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            {/* Loading state */}
            {rotationsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Error state */}
            {rotationsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {rotationsError}
              </Alert>
            )}

            {/* Rotations list */}
            {!rotationsLoading && !rotationsError && (
              <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                {filteredRotations.length > 0 ? (
                  filteredRotations.map((rotation) => (
                    <EntityCard
                      key={rotation.rotation_number}
                      entity={rotation}
                      type="rotation"
                      onClick={() => handleRotationClick(rotation)}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center">
                    No rotations found
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right side: Timecards */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Receipt color="primary" />
              <Typography variant="h5">
                Timecards
              </Typography>
              <Chip 
                label={filteredTimecards.length} 
                size="small" 
                color="primary" 
              />
            </Box>

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search timecards..."
              value={timecardsSearch}
              onChange={(e) => setTimecardsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            {/* Loading state */}
            {timecardsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Error state */}
            {timecardsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {timecardsError}
              </Alert>
            )}

            {/* Timecards list */}
            {!timecardsLoading && !timecardsError && (
              <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                {filteredTimecards.length > 0 ? (
                  filteredTimecards.map((timecard) => (
                    <EntityCard
                      key={timecard.timecard_number}
                      entity={timecard}
                      type="timecard"
                      onClick={() => handleTimecardClick(timecard)}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center">
                    No timecards found
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuditLanding;
